import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, CheckCircle, Phone, Star, Shield, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';

// ── 3-Layer JSON-LD Schema ──────────────────────────────────────────────────
const schemaMarkup = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "LocalBusiness",
      "@id": "https://sotsvc.com/#business",
      "name": "Sonz of Thunder Services",
      "alternateName": "SOTSVC",
      "description": "Professional residential and commercial cleaning services in Altamonte Springs, FL. We Bring the Boom to Every Room.",
      "url": "https://sotsvc.com",
      "telephone": "+1-407-461-6039",
      "priceRange": "$$",
      "image": "https://sotsvc.com/og/sotsvc-logo.png",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Altamonte Springs",
        "addressRegion": "FL",
        "postalCode": "32701",
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 28.6611,
        "longitude": -81.3656
      },
      "areaServed": {
        "@type": "City",
        "name": "Altamonte Springs",
        "sameAs": "https://en.wikipedia.org/wiki/Altamonte_Springs,_Florida"
      },
      "openingHoursSpecification": [
        { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"], "opens": "07:00", "closes": "19:00" },
        { "@type": "OpeningHoursSpecification", "dayOfWeek": "Sunday", "opens": "09:00", "closes": "17:00" }
      ],
      "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "87" }
    },
    {
      "@type": "Service",
      "serviceType": "Cleaning Service",
      "provider": { "@id": "https://sotsvc.com/#business" },
      "areaServed": { "@type": "City", "name": "Altamonte Springs, FL" },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Cleaning Services in Altamonte Springs",
        "itemListElement": [
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Residential House Cleaning" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Commercial Office Cleaning" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Deep Cleaning Service" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Move-In / Move-Out Cleaning" } }
        ]
      }
    },
    {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://sotsvc.com" },
        { "@type": "ListItem", "position": 2, "name": "Service Areas", "item": "https://sotsvc.com/service-areas" },
        { "@type": "ListItem", "position": 3, "name": "Altamonte Springs", "item": "https://sotsvc.com/cleaning-services/altamonte-springs-fl" }
      ]
    }
  ]
};

const services = [
  { name: 'Residential House Cleaning', desc: 'Weekly, bi-weekly, or monthly home cleaning tailored to your schedule.' },
  { name: 'Commercial Office Cleaning', desc: 'Professional cleaning for offices, retail, and commercial spaces.' },
  { name: 'Deep Cleaning', desc: 'Top-to-bottom intensive cleaning — baseboards, appliances, and beyond.' },
  { name: 'Move-In / Move-Out Cleaning', desc: 'Leave it spotless for the next chapter — guaranteed.' },
];

const neighborhoods = [
  'Cranes Roost', 'Lake Orienta', 'Altamonte Village', 'Spring Valley',
  'Brantley', 'San Sebastian', 'Altamonte Springs Town Center',
];

const zipCodes = ['32701', '32714'];

export default function AltamonteSpringsPage() {
  useEffect(() => {
    // Inject JSON-LD schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schemaMarkup);
    document.head.appendChild(script);
    // Update page meta
    document.title = 'Cleaning Services in Altamonte Springs, FL | Sonz of Thunder Services';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Professional residential and commercial cleaning services in Altamonte Springs, FL. Serving ZIP codes 32701 & 32714. We Bring the Boom to Every Room. Call SOTSVC today!');
    return () => { document.head.removeChild(script); };
  }, []);

  return (
    <div className="min-h-screen pt-20 bg-white">

      {/* ── HERO ───────────────────────────────────────────────────── */}
      <section className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="text-sm text-gray-400 mb-6">
            <Link to="/" className="hover:text-yellow-400">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/service-areas" className="hover:text-yellow-400">Service Areas</Link>
            <span className="mx-2">/</span>
            <span className="text-yellow-400">Altamonte Springs</span>
          </nav>
          <div className="flex items-center gap-3 mb-4">
            <MapPin className="h-7 w-7 text-yellow-400" />
            <span className="text-yellow-400 font-semibold uppercase tracking-widest text-sm">Seminole County, FL</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4 leading-tight">
            Cleaning Services in<br />
            <span className="text-yellow-400">Altamonte Springs, FL</span>
          </h1>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl">
            Sonz of Thunder Services proudly serves Altamonte Springs and surrounding Seminole County communities.
            Residential, commercial, and deep cleaning — done right, every time.
          </p>
          <p className="text-lg font-bold text-yellow-400 tracking-wide mb-8">
            "We Bring the Boom to Every Room"
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button variant="gold" size="lg" asChild>
              <Link to="/contact">Get a Free Quote</Link>
            </Button>
            <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-gray-900" asChild>
              <a href="tel:+14074616039"><Phone className="h-4 w-4 mr-2" /> Call Now</a>
            </Button>
          </div>
        </div>
      </section>

      {/* ── TRUST BAR ──────────────────────────────────────────────── */}
      <section className="bg-yellow-400 py-6">
        <div className="max-w-5xl mx-auto px-4 flex flex-wrap justify-center gap-8 text-gray-900 font-semibold text-sm">
          <span className="flex items-center gap-2"><Star className="h-4 w-4" /> 4.9★ Rated in Altamonte Springs</span>
          <span className="flex items-center gap-2"><Shield className="h-4 w-4" /> Licensed & Insured</span>
          <span className="flex items-center gap-2"><CheckCircle className="h-4 w-4" /> Satisfaction Guaranteed</span>
          <span className="flex items-center gap-2"><Clock className="h-4 w-4" /> Same-Week Availability</span>
        </div>
      </section>

      {/* ── SERVICES ───────────────────────────────────────────────── */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">
            Cleaning Services We Offer in Altamonte Springs
          </h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            Whether you're a homeowner near Cranes Roost Park or a business in the Altamonte Springs Town Center,
            we have a service built for you.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((s) => (
              <div key={s.name} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-yellow-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">{s.name}</h3>
                    <p className="text-gray-600 text-sm">{s.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── LOCAL NEIGHBORHOODS ────────────────────────────────────── */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Neighborhoods We Serve in Altamonte Springs
          </h2>
          <p className="text-gray-600 mb-8">
            We clean homes and businesses throughout Altamonte Springs, including ZIP codes{' '}
            <strong>{zipCodes.join(' and ')}</strong>. Our team knows Seminole County — no learning curve.
          </p>
          <div className="flex flex-wrap gap-3">
            {neighborhoods.map((n) => (
              <span key={n} className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 rounded-full px-4 py-2 text-sm font-medium">
                <MapPin className="h-3.5 w-3.5 text-yellow-500" /> {n}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY SOTSVC ─────────────────────────────────────────────── */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Why Altamonte Springs Trusts Sonz of Thunder
          </h2>
          <p className="text-gray-400 mb-12 max-w-2xl mx-auto">
            We're not a franchise. We're a locally-owned Central Florida business that shows up,
            does the work, and backs it with a guarantee.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: '⚡', title: 'We Bring the Boom', body: 'Every room gets our full attention. No rush jobs, no skipped corners.' },
              { icon: '🛡️', title: 'Fully Insured', body: 'Licensed, bonded, and insured. Your home or business is protected.' },
              { icon: '🕊️', title: 'Faith-Driven Service', body: 'We operate with integrity. Colossians 3:23 is how we work — with our whole heart.' },
            ].map((item) => (
              <div key={item.title} className="bg-gray-800 rounded-xl p-8">
                <div className="text-4xl mb-4">{item.icon}</div>
                <h3 className="text-xl font-bold mb-2 text-yellow-400">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ────────────────────────────────────────────────────── */}
      <section className="py-20 bg-yellow-400">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">
            Ready for a Cleaner Altamonte Springs Home or Office?
          </h2>
          <p className="text-gray-800 mb-8 text-lg">
            Get your free, no-obligation quote today. Same-week availability. YOU'RE CLEAN OR YOU'RE DIRTY.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-gray-900 text-white hover:bg-gray-800" asChild>
              <Link to="/contact">Get a Free Quote</Link>
            </Button>
            <Button size="lg" variant="outline" className="border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white" asChild>
              <Link to="/services">View All Services</Link>
            </Button>
          </div>
          <p className="text-xs text-gray-700 mt-6">
            Serving Altamonte Springs, FL · ZIP 32701 & 32714 · sotsvc.com · trustedcleaningexpert.com
          </p>
        </div>
      </section>

    </div>
  );
}
